import Rollbar from "lib/rollbar";
import NProgress from "vendor/nprogress/nprogress";
const PromiseThrottle = require("promise-throttle");

const JSON_TYPE = "json";
const FORM_DATA_TYPE = "form";

const promiseThrottle = new PromiseThrottle({
  requestsPerSecond: 1, // up to 1 request per second
  promiseImplementation: Promise // the Promise library you are using
});

const isOnline = error => {
  return (
    Object.prototype.hasOwnProperty.call(error, "stack") &&
    error.stack.includes("TypeError") &&
    Object.prototype.hasOwnProperty.call(error, "fileName")
  );
};

const checkStatus = async response => {
  if (response.status >= 200 && response.status < 304) {
    return response;
  }

  if (
    Object.prototype.hasOwnProperty.call(response, "text") &&
    typeof response.text === "function"
  ) {
    return Promise.resolve(response.text()).then(msg => {
      throw new Error(msg);
    });
  }

  let pending;
  let errorMessage;
  try {
    pending = response.text();
  } catch {
    pending = Promise.resolve(response);
  }
  await pending.then(msg => {
    errorMessage = msg;
  });
  throw errorMessage;
};

const { fetch } = window;

const ajax = function ajaxF(type, method, url, payload, successFn, errorFn) {
  return new Promise((resolve, reject) => {
    NProgress.start();
    const { $ } = window;

    const token = $('meta[name="csrf-token"]').attr("content");

    const defaults = {
      credentials: "same-origin",
      method: method
    };

    if (type === JSON_TYPE) {
      let locale = 'en'
      if (window.appConfig) {
        locale = window.appConfig.locale
      }
      defaults.headers = {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": token,
        'X-HH-Language': locale
      };
      defaults.body = JSON.stringify(payload);
    } else if (type === FORM_DATA_TYPE) {
      if (typeof payload.append === "function") {
        defaults.body = payload;
      } else {
        // payload should be in FormData object
        let formData = new FormData();
        for (let key in payload) {
          formData.append(key, payload[key]);
        }

        defaults.body = formData;
      }
      defaults.headers = {
        "X-CSRF-Token": token
      };
    }
    return fetch(url, defaults)
      .then(response => {
        NProgress.set(0.4);
        return checkStatus(response);
      })
      .then(response => {
        NProgress.set(0.8);
        return response.json();
      })
      .then(body => {
        NProgress.done();
        if (typeof successFn === "function") {
          resolve(successFn(body));
        }
      })
      .catch(error => {
        NProgress.done();
        Rollbar.error(error);
        if (typeof errorFn === "function") {
          reject(errorFn(error));
        } else {
          reject(error);
        }
      });
  });
};

/*
Usage:

import ajax from 'this file'

    url = 'http://xxxx.com'
    success = function() {
    }
    error = function() {
    }
    ajax(url, success, error)
 */
const get = function getF(url, successFn, errorFn, bodyType) {
  return new Promise((resolve, reject) => {
    NProgress.start();
    const headerLang = `${
      window.appConfig.locale
    }-${window.appConfig.locale.toUpperCase()}`;
    fetch(url, {
      credentials: "same-origin",
      headers: {
        "X-HH-Language": headerLang
      }
    })
      .then(response => {
        NProgress.set(0.4);
        return checkStatus(response);
      })
      .then(response => {
        NProgress.set(0.8);
        if (bodyType === "json") {
          return response.json();
        }
        if (bodyType === "text") {
          return response.text();
        }
        Rollbar.error("Unimplemented error", {
          url,
          bodyType,
          response
        });
        return "";
      })
      .then(body => {
        NProgress.done();
        if (typeof successFn === "function") {
          resolve(successFn(body));
        }
      })
      .catch(error => {
        NProgress.done();
        if (bodyType === "json") {
          if (isOnline(error)) {
            Rollbar.error(error);
            if (typeof errorFn === "function") {
              reject(errorFn(error));
            } else {
              reject(error);
            }
          }
        }

        if (bodyType === "text") {
          if (typeof errorFn === "function") {
            reject(errorFn(error));
          } else {
            reject(error);
          }
        }
      });
  });
};

/*
Usage:

  import patchJSON from 'this file'
  let url = 'http://xxxx.com'
  let payload = { foo: 'bar' }
  let success = function() {
  }
  let error = function() {
  }
  patchJSON(url, payload, success, error)
*/
export const patchJSON = (url, payload, successFn, errorFn) => {
  const runAjax = () => {
    return ajax(JSON_TYPE, "PATCH", url, payload, successFn, errorFn);
  };
  promiseThrottle.add(runAjax);
};

export const putFormData = (url, payload, successFn, errorFn) => {
  const runAjax = () => {
    return ajax(FORM_DATA_TYPE, "PUT", url, payload, successFn, errorFn);
  };
  promiseThrottle.add(runAjax);
};
/*
Usage:

  import patchFormData from 'this file'
  let url = 'http://xxxx.com'

  let payload = new FormData();
  let fileField = document.querySelector("input[type='file']");
  payload.append('username', 'abc123');
  payload.append('avatar', fileField.files[0]);

  let success = function() {
  }
  let error = function() {
  }
  patchJSON(url, payload, success, error)
*/
export const patchFormData = (url, payload, successFn, errorFn) => {
  const runAjax = () => {
    return ajax(FORM_DATA_TYPE, "PATCH", url, payload, successFn, errorFn);
  };
  promiseThrottle.add(runAjax);
};

export const getJSON = function getJSON(url, successFn, errorFn) {
  const runAjax = () => {
    return get(url, successFn, errorFn, "json");
  };
  promiseThrottle.add(runAjax);
};

export const getText = function getText(url, successFn, errorFn) {
  const runAjax = () => {
    return get(url, successFn, errorFn, "text");
  };
  promiseThrottle.add(runAjax);
};

export const postJSON = (url, payload, successFn, errorFn) => {
  const runAjax = () => {
    return ajax(JSON_TYPE, "POST", url, payload, successFn, errorFn);
  };
  promiseThrottle.add(runAjax);
};

export const postFormData = (url, payload, successFn, errorFn) => {
  const runAjax = () => {
    return ajax(FORM_DATA_TYPE, "POST", url, payload, successFn, errorFn);
  };
  promiseThrottle.add(runAjax);
};

export const deleteJSON = (url, payload, successFn, errorFn) => {
  const runAjax = () => {
    return ajax(JSON_TYPE, "DELETE", url, payload, successFn, errorFn);
  };
  promiseThrottle.add(runAjax);
};
